// Mapbox-Geocoding-backed address/place autocomplete. Pure fetch — no extra
// npm dep. Used by both /buy (city/neighborhood/ZIP) and /sell (street address).
//
// Props:
//   value, onChange(text), onPick(feature) — feature is a Mapbox GeoJSON feature
//   onKeyDown — passthrough; runs when no dropdown navigation consumes the key
//   types     — e.g. "address" or "place,locality,neighborhood,postcode"
//   country   — default "us"
//   limit     — default 5
//   proximity — optional [lng,lat] to bias suggestions
//   placeholder, className (wrap), inputClassName (input), disabled, autoFocus
//
// Falls back to a plain input if REACT_APP_MAPBOX_TOKEN is missing.

import { useEffect, useRef, useState } from "react";

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || "";

// ─── One-time style injection ────────────────────────────────────────────────
const STYLES_ID = "aac-styles";
const CSS = `
  .aac-wrap{position:relative}
  .aac-wrap > input{width:100%}
  .aac-list{position:absolute;top:calc(100% + 4px);left:0;right:0;background:#fff;border:1px solid #d8ede6;border-radius:10px;box-shadow:0 8px 28px rgba(0,0,0,0.12);z-index:50;max-height:280px;overflow-y:auto}
  .aac-item{padding:10px 14px;cursor:pointer;font-size:13px;color:#1c2b25;border-bottom:1px solid #f0f4f2;display:flex;flex-direction:column;gap:2px;background:#fff;border-left:none;border-right:none;border-top:none;text-align:left;width:100%;font-family:inherit}
  .aac-item:last-child{border-bottom:none}
  .aac-item:hover, .aac-item.active{background:#f2f8f5}
  .aac-item-text{font-weight:600;color:#152d21}
  .aac-item-context{font-size:11px;color:#9aa9a0;font-family:'DM Mono',monospace;letter-spacing:0.04em}
  .aac-empty{padding:12px 14px;color:#9aa9a0;font-style:italic;font-size:13px}
  .aac-loading{padding:10px 14px;font-size:11px;color:#7aad98;font-family:'DM Mono',monospace;letter-spacing:0.06em}
`;
if (typeof document !== "undefined" && !document.getElementById(STYLES_ID)) {
  const el = document.createElement("style");
  el.id = STYLES_ID;
  el.textContent = CSS;
  document.head.appendChild(el);
}

// Format a one-line context line under the bold primary text.
const contextLabel = (f) => {
  // Mapbox `place_name` already reads as "Bold text, context". Strip the
  // primary text from the front so we don't repeat it.
  if (!f.place_name || !f.text) return f.place_name || "";
  return f.place_name.startsWith(f.text + ", ")
    ? f.place_name.slice(f.text.length + 2)
    : f.place_name;
};

export default function AddressAutocomplete({
  value,
  onChange,
  onPick,
  onKeyDown,
  types = "address",
  country = "us",
  limit = 5,
  proximity,
  placeholder,
  className,
  inputClassName,
  disabled,
  autoFocus,
  required,
  name,
  type = "text",
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const abortRef = useRef(null);
  const timerRef = useRef(null);
  const skipFetchRef = useRef(false);

  useEffect(() => {
    if (!MAPBOX_TOKEN) return;
    // After picking a suggestion we update value but don't want to re-fetch.
    if (skipFetchRef.current) {
      skipFetchRef.current = false;
      return;
    }
    if (!value || value.length < 3) {
      setSuggestions([]); setOpen(false); setLoading(false);
      return;
    }
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      if (abortRef.current) abortRef.current.abort();
      abortRef.current = new AbortController();
      setLoading(true);
      try {
        const url = new URL(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(value)}.json`
        );
        url.searchParams.set("access_token", MAPBOX_TOKEN);
        url.searchParams.set("autocomplete", "true");
        url.searchParams.set("country", country);
        url.searchParams.set("types", types);
        url.searchParams.set("limit", String(limit));
        if (proximity) url.searchParams.set("proximity", proximity.join(","));
        const res = await fetch(url, { signal: abortRef.current.signal });
        const data = await res.json();
        setSuggestions(data.features || []);
        setOpen(true);
        setActive(-1);
      } catch (e) {
        if (e.name !== "AbortError") setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [value, types, country, limit, proximity?.join(",")]);

  // Close dropdown on outside click.
  useEffect(() => {
    const onDoc = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (f) => {
    skipFetchRef.current = true;
    onChange?.(f.place_name);
    onPick?.(f);
    setOpen(false);
    setSuggestions([]);
  };

  const handleKey = (e) => {
    if (open && suggestions.length > 0) {
      if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(suggestions.length - 1, i + 1)); return; }
      if (e.key === "ArrowUp")   { e.preventDefault(); setActive((i) => Math.max(0, i - 1)); return; }
      if (e.key === "Enter" && active >= 0) {
        e.preventDefault();
        pick(suggestions[active]);
        return;
      }
      if (e.key === "Escape") { setOpen(false); return; }
    }
    onKeyDown?.(e);
  };

  return (
    <div ref={wrapRef} className={`aac-wrap${className ? " " + className : ""}`}>
      <input
        className={inputClassName}
        value={value || ""}
        onChange={(e) => { onChange?.(e.target.value); if (e.target.value.length >= 3) setOpen(true); }}
        onFocus={() => { if (suggestions.length > 0) setOpen(true); }}
        onKeyDown={handleKey}
        placeholder={placeholder}
        disabled={disabled}
        autoFocus={autoFocus}
        autoComplete="off"
        required={required}
        name={name}
        type={type}
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
      />
      {open && MAPBOX_TOKEN && (loading || suggestions.length > 0 || (value && value.length >= 3)) && (
        <div className="aac-list" role="listbox">
          {loading && <div className="aac-loading">Searching…</div>}
          {!loading && suggestions.map((f, i) => (
            <button
              type="button"
              key={f.id}
              className={`aac-item${i === active ? " active" : ""}`}
              onMouseEnter={() => setActive(i)}
              onMouseDown={(e) => { e.preventDefault(); pick(f); }}
              role="option"
              aria-selected={i === active}
            >
              <span className="aac-item-text">{f.text}</span>
              <span className="aac-item-context">{contextLabel(f)}</span>
            </button>
          ))}
          {!loading && suggestions.length === 0 && value && value.length >= 3 && (
            <div className="aac-empty">No matches.</div>
          )}
        </div>
      )}
    </div>
  );
}
